/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { PageProductSearchResponse } from '../models/page-product-search-response';
import { PageProductShopsPageResponse } from '../models/page-product-shops-page-response';
import { ProductCollectorResponse } from '../models/product-collector-response';
import { ProductSearchDetailsFilter } from '../models/product-search-details-filter';
import { ProductVariantResponse } from '../models/product-variant-response';
import { SearchTextExtension } from '../models/search-text-extension';

/**
 * Product search endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductSearchService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation searchProduct
   */
  static readonly SearchProductPath = '/search/products';

  /**
   * Search products by filter.
   *
   * Search products by the specified ProductSearchDetails filter object
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchProduct()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchProduct$Response(
    params: {
      /**
       * Determines which logic will be used BUYING/SELLING
       */
      marketplaceSection: 'BUYING' | 'SELLING';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
      body: ProductSearchDetailsFilter;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageProductSearchResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSearchService.SearchProductPath,
      'post'
    );
    if (params) {
      rb.query('marketplaceSection', params.marketplaceSection, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageProductSearchResponse>;
        })
      );
  }

  /**
   * Search products by filter.
   *
   * Search products by the specified ProductSearchDetails filter object
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `searchProduct$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchProduct(
    params: {
      /**
       * Determines which logic will be used BUYING/SELLING
       */
      marketplaceSection: 'BUYING' | 'SELLING';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
      body: ProductSearchDetailsFilter;
    },
    context?: HttpContext
  ): Observable<PageProductSearchResponse> {
    return this.searchProduct$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageProductSearchResponse>) =>
          r.body as PageProductSearchResponse
      )
    );
  }

  /**
   * Path part for operation getBrandsForSearchResult
   */
  static readonly GetBrandsForSearchResultPath = '/search/products/brands';

  /**
   * Get all brands by filter.
   *
   * Get all possible brand values by the specified ProductSearchDetails filter object
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getBrandsForSearchResult()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getBrandsForSearchResult$Response(
    params: {
      /**
       * Determines which logic will be used BUYING/SELLING
       */
      marketplaceSection: 'BUYING' | 'SELLING';
      body: ProductSearchDetailsFilter;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<string>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSearchService.GetBrandsForSearchResultPath,
      'post'
    );
    if (params) {
      rb.query('marketplaceSection', params.marketplaceSection, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<string>>;
        })
      );
  }

  /**
   * Get all brands by filter.
   *
   * Get all possible brand values by the specified ProductSearchDetails filter object
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getBrandsForSearchResult$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getBrandsForSearchResult(
    params: {
      /**
       * Determines which logic will be used BUYING/SELLING
       */
      marketplaceSection: 'BUYING' | 'SELLING';
      body: ProductSearchDetailsFilter;
    },
    context?: HttpContext
  ): Observable<Array<string>> {
    return this.getBrandsForSearchResult$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<string>>) => r.body as Array<string>)
    );
  }

  /**
   * Path part for operation getPackagingUnitId
   */
  static readonly GetPackagingUnitIdPath = '/search/products/{packagingUnitId}';

  /**
   * Get data from the specified packagingUnitId.
   *
   * Get data from the specified packagingUnitId
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPackagingUnitId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackagingUnitId$Response(
    params: {
      /**
       * Specified packagingUnitId
       */
      packagingUnitId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ProductVariantResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSearchService.GetPackagingUnitIdPath,
      'get'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ProductVariantResponse>;
        })
      );
  }

  /**
   * Get data from the specified packagingUnitId.
   *
   * Get data from the specified packagingUnitId
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPackagingUnitId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackagingUnitId(
    params: {
      /**
       * Specified packagingUnitId
       */
      packagingUnitId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<ProductVariantResponse> {
    return this.getPackagingUnitId$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ProductVariantResponse>) =>
          r.body as ProductVariantResponse
      )
    );
  }

  /**
   * Path part for operation getVariantsByPackagingUnitId
   */
  static readonly GetVariantsByPackagingUnitIdPath =
    '/search/products/{packagingUnitId}/variants';

  /**
   * Get all variants by specified packagingUnitId.
   *
   * Get all variants by specified packagingUnitId
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getVariantsByPackagingUnitId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getVariantsByPackagingUnitId$Response(
    params: {
      /**
       * Specified packagingUnitId
       */
      packagingUnitId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<ProductVariantResponse>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSearchService.GetVariantsByPackagingUnitIdPath,
      'get'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<ProductVariantResponse>>;
        })
      );
  }

  /**
   * Get all variants by specified packagingUnitId.
   *
   * Get all variants by specified packagingUnitId
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getVariantsByPackagingUnitId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getVariantsByPackagingUnitId(
    params: {
      /**
       * Specified packagingUnitId
       */
      packagingUnitId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<Array<ProductVariantResponse>> {
    return this.getVariantsByPackagingUnitId$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<ProductVariantResponse>>) =>
          r.body as Array<ProductVariantResponse>
      )
    );
  }

  /**
   * Path part for operation getShopsByPackagingUnitId
   */
  static readonly GetShopsByPackagingUnitIdPath =
    '/search/products/{packagingUnitId}/shops';

  /**
   * Get the shop based prices by specified packagingUnitId.
   *
   * Get the shop based prices by specified packagingUnitId
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getShopsByPackagingUnitId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getShopsByPackagingUnitId$Response(
    params: {
      /**
       * Specified packagingUnitId
       */
      packagingUnitId: string;

      /**
       * Filter on minimum price
       */
      minPrice?: number;

      /**
       * Filter on maximum price
       */
      maxPrice?: number;

      /**
       * Filter on possible payment methods
       */
      paymentMethods?: Array<
        'CASH' | 'CARD' | 'BANK_TRANSFER' | 'CASH_ON_DELIVERY'
      >;

      /**
       * Filter on deliveryServiceAvailable
       */
      deliveryServiceAvailable?: boolean;

      /**
       * Filter on onSiteServiceAvailable
       */
      onSiteServiceAvailable?: boolean;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageProductShopsPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSearchService.GetShopsByPackagingUnitIdPath,
      'get'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
      rb.query('minPrice', params.minPrice, {});
      rb.query('maxPrice', params.maxPrice, {});
      rb.query('paymentMethods', params.paymentMethods, {});
      rb.query('deliveryServiceAvailable', params.deliveryServiceAvailable, {});
      rb.query('onSiteServiceAvailable', params.onSiteServiceAvailable, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageProductShopsPageResponse>;
        })
      );
  }

  /**
   * Get the shop based prices by specified packagingUnitId.
   *
   * Get the shop based prices by specified packagingUnitId
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getShopsByPackagingUnitId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getShopsByPackagingUnitId(
    params: {
      /**
       * Specified packagingUnitId
       */
      packagingUnitId: string;

      /**
       * Filter on minimum price
       */
      minPrice?: number;

      /**
       * Filter on maximum price
       */
      maxPrice?: number;

      /**
       * Filter on possible payment methods
       */
      paymentMethods?: Array<
        'CASH' | 'CARD' | 'BANK_TRANSFER' | 'CASH_ON_DELIVERY'
      >;

      /**
       * Filter on deliveryServiceAvailable
       */
      deliveryServiceAvailable?: boolean;

      /**
       * Filter on onSiteServiceAvailable
       */
      onSiteServiceAvailable?: boolean;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageProductShopsPageResponse> {
    return this.getShopsByPackagingUnitId$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageProductShopsPageResponse>) =>
          r.body as PageProductShopsPageResponse
      )
    );
  }

  /**
   * Path part for operation getCollectorsByPackagingUnitId
   */
  static readonly GetCollectorsByPackagingUnitIdPath =
    '/search/products/{packagingUnitId}/collectors';

  /**
   * Get all collectors by specified packagingUnitId.
   *
   * Get all collectors by specified packagingUnitId
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCollectorsByPackagingUnitId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCollectorsByPackagingUnitId$Response(
    params: {
      /**
       * Specified packagingUnitId
       */
      packagingUnitId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<ProductCollectorResponse>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSearchService.GetCollectorsByPackagingUnitIdPath,
      'get'
    );
    if (params) {
      rb.path('packagingUnitId', params.packagingUnitId, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<ProductCollectorResponse>>;
        })
      );
  }

  /**
   * Get all collectors by specified packagingUnitId.
   *
   * Get all collectors by specified packagingUnitId
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCollectorsByPackagingUnitId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCollectorsByPackagingUnitId(
    params: {
      /**
       * Specified packagingUnitId
       */
      packagingUnitId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<Array<ProductCollectorResponse>> {
    return this.getCollectorsByPackagingUnitId$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<ProductCollectorResponse>>) =>
          r.body as Array<ProductCollectorResponse>
      )
    );
  }

  /**
   * Path part for operation getProductNamesAndBrands
   */
  static readonly GetProductNamesAndBrandsPath =
    '/search/products/names-and-brands';

  /**
   * Get product names and brands based on the given parameter.
   *
   * If the parameter is present in both the name and the brand of the same product, then 2 results will be shown.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProductNamesAndBrands()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductNamesAndBrands$Response(
    params: {
      /**
       * Search string given by the user in the product search field.
       */
      searchText: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<SearchTextExtension>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSearchService.GetProductNamesAndBrandsPath,
      'get'
    );
    if (params) {
      rb.query('searchText', params.searchText, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<SearchTextExtension>>;
        })
      );
  }

  /**
   * Get product names and brands based on the given parameter.
   *
   * If the parameter is present in both the name and the brand of the same product, then 2 results will be shown.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProductNamesAndBrands$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductNamesAndBrands(
    params: {
      /**
       * Search string given by the user in the product search field.
       */
      searchText: string;
    },
    context?: HttpContext
  ): Observable<Array<SearchTextExtension>> {
    return this.getProductNamesAndBrands$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<SearchTextExtension>>) =>
          r.body as Array<SearchTextExtension>
      )
    );
  }
}
